import { useEffect, useState, type FormEvent } from 'react';
import { useStore } from '@nanostores/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  CheckboxInput,
  EmptyState,
  HStack,
  Heading,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  TextInput,
  VStack,
  useToast,
} from '@astryxdesign/core';

import { formatDateTime, type ApiKey } from '../lib/api';
import { $auth } from '../stores/auth';
import {
  $keys,
  $keysError,
  $keysLoading,
  $maxKeys,
  $revealedKey,
  createKey,
  dismissRevealedKey,
  loadKeys,
  revokeKey,
} from '../stores/keys';

export function KeysPage() {
  const keys = useStore($keys);
  const maxKeys = useStore($maxKeys);
  const loading = useStore($keysLoading);
  const error = useStore($keysError);
  const revealed = useStore($revealedKey);
  const auth = useStore($auth);
  const toast = useToast();

  const [name, setName] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    void loadKeys();
  }, []);

  const activeCount = keys.filter((k) => !k.revoked).length;
  const atLimit = maxKeys > 0 && activeCount >= maxKeys;

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      await createKey(name.trim(), isTest);
      setName('');
      setIsTest(false);
    } catch {
      /* rendered from the store */
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(key: ApiKey) {
    // Revocation is immediate and irreversible; make the caller confirm.
    const ok = window.confirm(
      `Revoke "${key.name}" (${key.prefix}…${key.last4})?\n\nAny service still using this key will start getting 401 responses immediately. This cannot be undone.`,
    );
    if (!ok) return;

    setRevoking(key.id);
    try {
      await revokeKey(key.id);
      toast({ body: 'Key revoked.' });
    } catch {
      /* rendered from the store */
    } finally {
      setRevoking(null);
    }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast({ body: 'Copied to clipboard.' });
    } catch {
      toast({ type: 'error', body: 'Could not copy — select the key and copy it manually.' });
    }
  }

  return (
    <VStack gap={3}>
      <VStack gap={0.5}>
        <Heading level={1}>API keys</Heading>
        <Text color="secondary">
          {maxKeys > 0
            ? `${activeCount} of ${maxKeys} active keys on the ${auth.plan?.name ?? 'current'} plan.`
            : `${activeCount} active keys.`}
        </Text>
      </VStack>

      {error && <Banner status="error" title="Key operation failed" description={error} />}

      {/* A one-time secret must be visible on sight, so this is a plain card
          rather than a Banner (whose children collapse behind a toggle). */}
      {revealed?.key && (
        <Card padding={3} variant="green">
          <VStack gap={1.5}>
            <HStack gap={2} justify="between" vAlign="center" wrap="wrap">
              <Text weight="semibold">Your new key — copy it now</Text>
              <Button label="Dismiss" variant="ghost" size="sm" onClick={dismissRevealedKey} />
            </HStack>
            <Text size="sm" color="secondary">
              This is the only time the full key is shown. We store a one-way hash, so it cannot be
              recovered later.
            </Text>
            <div className="key-reveal">
              <span className="mono">{revealed.key}</span>
              <Button
                label="Copy"
                size="sm"
                variant="primary"
                onClick={() => void copy(revealed.key!)}
              />
            </div>
          </VStack>
        </Card>
      )}

      <Card padding={3} variant="default">
        <form onSubmit={onCreate}>
          <VStack gap={2}>
            <Text weight="semibold">Create a key</Text>
            <TextInput
              label="Label"
              value={name}
              onChange={setName}
              placeholder="production-forecast-job"
              description="For your own reference — shown in the list and in request logs."
              width="100%"
            />
            <HStack gap={3} wrap="wrap" vAlign="center" justify="between">
              <CheckboxInput
                label="Issue a test key (vct_test_…)"
                description="Same limits, but easy to tell apart from production credentials."
                value={isTest}
                onChange={setIsTest}
              />
              <Button
                label="Create key"
                variant="primary"
                type="submit"
                isLoading={creating}
                isDisabled={atLimit}
              />
            </HStack>
            {atLimit && (
              <Text size="sm" color="secondary">
                You have reached this plan&apos;s key limit. Revoke an unused key, or upgrade for more.
              </Text>
            )}
          </VStack>
        </form>
      </Card>

      {loading && keys.length === 0 ? (
        <Spinner size="lg" label="Loading keys" />
      ) : keys.length === 0 ? (
        <EmptyState
          title="No API keys yet"
          description="Create your first key above, then call POST /v1/forecast with it."
        />
      ) : (
        <Table density="balanced" dividers="rows" hasHover>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Label</TableHeaderCell>
              <TableHeaderCell>Key</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell>Last used</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((k) => (
              <TableRow key={k.id}>
                <TableCell>
                  <Text size="sm" weight="medium">
                    {k.name}
                  </Text>
                </TableCell>
                <TableCell>
                  <span className="mono">
                    {k.prefix}…{k.last4}
                  </span>
                </TableCell>
                <TableCell>
                  {k.revoked ? (
                    <Badge label="Revoked" variant="error" />
                  ) : (
                    <Badge label="Active" variant="success" />
                  )}
                </TableCell>
                <TableCell>
                  <Text size="sm" color="secondary">
                    {formatDateTime(k.created_at)}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text size="sm" color="secondary">
                    {k.last_used_at ? formatDateTime(k.last_used_at) : 'Never'}
                  </Text>
                </TableCell>
                <TableCell>
                  {!k.revoked && (
                    <Button
                      label="Revoke"
                      variant="destructive"
                      size="sm"
                      isLoading={revoking === k.id}
                      onClick={() => void onRevoke(k)}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </VStack>
  );
}
